import PptxGenJS from 'pptxgenjs';
import jsPDF from 'jspdf';
import { Deck, SlideContent } from '../types';
import { HEALTHCARE_COLORS } from '../constants';

// Convert our color constants to hex for PowerPoint
const colorMap = {
  [HEALTHCARE_COLORS.offWhite]: 'FAFAF7',
  [HEALTHCARE_COLORS.softBlack]: '111111',
  [HEALTHCARE_COLORS.deepSlate]: '2C3E50',
  [HEALTHCARE_COLORS.mutedTeal]: '5D8A8A',
  [HEALTHCARE_COLORS.forestGreen]: '6B8E65',
  [HEALTHCARE_COLORS.warmGray]: '8B8680',
};

export class DeckExporter {
  private deck: Deck;
  private pptx: PptxGenJS;

  constructor(deck: Deck) {
    this.deck = deck;
    this.pptx = new PptxGenJS();
    this.setupPresentationDefaults();
  }

  private setupPresentationDefaults() {
    // Set presentation properties
    this.pptx.author = 'HealthyMitra';
    this.pptx.company = 'HealthyMitra';
    this.pptx.title = this.deck.name;
    this.pptx.subject = `${this.deck.stakeholder} presentation deck`;

    // Set default slide layout (16:9) - use layout instead of slideSize
    this.pptx.layout = 'LAYOUT_16x9';

    // Define master layout
    this.pptx.defineSlideMaster({
      title: 'HEALTHCARE_MASTER',
      background: { color: colorMap[HEALTHCARE_COLORS.offWhite] || 'FAFAF7' },
      objects: [
        // Subtle branding in corner
        {
          text: {
            text: 'HealthyMitra',
            options: {
              x: 11.5,
              y: 6.8,
              w: 1.5,
              h: 0.3,
              fontSize: 8,
              color: colorMap[HEALTHCARE_COLORS.warmGray],
              fontFace: 'Inter',
              align: 'right'
            }
          }
        }
      ]
    });
  }

  private addStatementSlide(slide: SlideContent) {
    const slideObj = this.pptx.addSlide({ masterName: 'HEALTHCARE_MASTER' });
    const content = slide.content;

    // Calculate appropriate font size based on text length
    const textLength = content.mainStatement.length;
    let titleFontSize = 48;
    if (textLength > 60) titleFontSize = 36;
    if (textLength > 100) titleFontSize = 28;
    if (textLength > 150) titleFontSize = 24;

    // Main statement - use more space and smaller font for longer text
    slideObj.addText(content.mainStatement, {
      x: 0.5,
      y: 1.5,
      w: 12.5,
      h: 3,
      fontSize: titleFontSize,
      color: colorMap[HEALTHCARE_COLORS.softBlack],
      fontFace: 'Inter',
      align: 'center',
      valign: 'middle',
      bold: false,
      breakLine: true,
      wrap: true
    });

    // Supporting text if present
    if (content.supportingText) {
      const supportTextLength = content.supportingText.length;
      let supportFontSize = 20;
      if (supportTextLength > 80) supportFontSize = 18;
      if (supportTextLength > 120) supportFontSize = 16;

      slideObj.addText(content.supportingText, {
        x: 1,
        y: 4.8,
        w: 11.5,
        h: 2,
        fontSize: supportFontSize,
        color: colorMap[HEALTHCARE_COLORS.warmGray],
        fontFace: 'Inter',
        align: 'center',
        valign: 'top',
        breakLine: true,
        wrap: true
      });
    }

    // Speaker notes
    if (slide.speakerNotes) {
      slideObj.addNotes(slide.speakerNotes);
    }
  }

  private addTwoColumnSlide(slide: SlideContent) {
    const slideObj = this.pptx.addSlide({ masterName: 'HEALTHCARE_MASTER' });
    const content = slide.content;

    // Title
    if (slide.title) {
      slideObj.addText(slide.title, {
        x: 1,
        y: 0.5,
        w: 11.333,
        h: 1,
        fontSize: 36,
        color: colorMap[HEALTHCARE_COLORS.softBlack],
        fontFace: 'Inter',
        align: 'center',
        bold: true
      });
    }

    // Left column
    slideObj.addText(content.leftColumn.title, {
      x: 0.5,
      y: 2,
      w: 5.5,
      h: 0.8,
      fontSize: 24,
      color: colorMap[HEALTHCARE_COLORS.deepSlate],
      fontFace: 'Inter',
      bold: true
    });

    const leftContent = content.leftColumn.content.map((item: string) => `• ${item}`).join('\n');
    slideObj.addText(leftContent, {
      x: 0.5,
      y: 2.8,
      w: 5.5,
      h: 4,
      fontSize: 18,
      color: colorMap[HEALTHCARE_COLORS.softBlack],
      fontFace: 'Inter',
      lineSpacing: 24
    });

    // Right column
    slideObj.addText(content.rightColumn.title, {
      x: 7,
      y: 2,
      w: 5.5,
      h: 0.8,
      fontSize: 24,
      color: colorMap[HEALTHCARE_COLORS.deepSlate],
      fontFace: 'Inter',
      bold: true
    });

    const rightContent = content.rightColumn.content.map((item: string) => `• ${item}`).join('\n');
    slideObj.addText(rightContent, {
      x: 7,
      y: 2.8,
      w: 5.5,
      h: 4,
      fontSize: 18,
      color: colorMap[HEALTHCARE_COLORS.softBlack],
      fontFace: 'Inter',
      lineSpacing: 24
    });

    // Speaker notes
    if (slide.speakerNotes) {
      slideObj.addNotes(slide.speakerNotes);
    }
  }

  private addComparisonTableSlide(slide: SlideContent) {
    const slideObj = this.pptx.addSlide({ masterName: 'HEALTHCARE_MASTER' });
    const content = slide.content;

    // Title
    if (slide.title) {
      slideObj.addText(slide.title, {
        x: 1,
        y: 0.5,
        w: 11.333,
        h: 1,
        fontSize: 36,
        color: colorMap[HEALTHCARE_COLORS.softBlack],
        fontFace: 'Inter',
        align: 'center',
        bold: true
      });
    }

    // Build table data
    const tableData = [
      content.headers.map((header: string) => ({
        text: header,
        options: {
          fontSize: 18,
          bold: true,
          color: colorMap[HEALTHCARE_COLORS.deepSlate],
          fontFace: 'Inter'
        }
      }))
    ];

    // Add rows
    content.rows.forEach((row: any) => {
      const rowData = [
        {
          text: row.label,
          options: {
            fontSize: 16,
            bold: true,
            color: colorMap[HEALTHCARE_COLORS.softBlack],
            fontFace: 'Inter'
          }
        },
        ...row.values.map((value: string) => ({
          text: value,
          options: {
            fontSize: 16,
            color: colorMap[HEALTHCARE_COLORS.warmGray],
            fontFace: 'Inter'
          }
        }))
      ];
      tableData.push(rowData);
    });

    // Add table
    slideObj.addTable(tableData, {
      x: 1,
      y: 2,
      w: 11.333,
      h: 4.5,
      border: { type: 'none' },
      fill: { color: 'FFFFFF' },
      margin: 0.2
    });

    // Speaker notes
    if (slide.speakerNotes) {
      slideObj.addNotes(slide.speakerNotes);
    }
  }

  private addQuoteSlide(slide: SlideContent) {
    const slideObj = this.pptx.addSlide({ masterName: 'HEALTHCARE_MASTER' });
    const content = slide.content;

    // Calculate font size based on quote length
    const quoteLength = content.quote.length;
    let quoteFontSize = 28;
    if (quoteLength > 100) quoteFontSize = 24;
    if (quoteLength > 150) quoteFontSize = 20;
    if (quoteLength > 200) quoteFontSize = 18;

    // Quote text with quotation marks
    slideObj.addText(`"${content.quote}"`, {
      x: 0.8,
      y: 1.8,
      w: 11.8,
      h: 3.5,
      fontSize: quoteFontSize,
      color: colorMap[HEALTHCARE_COLORS.softBlack],
      fontFace: 'Inter',
      align: 'center',
      valign: 'middle',
      italic: true,
      lineSpacing: quoteFontSize + 8,
      breakLine: true,
      wrap: true
    });

    // Author attribution
    slideObj.addText(`— ${content.author}`, {
      x: 1.5,
      y: 5.4,
      w: 10.333,
      h: 0.8,
      fontSize: 18,
      color: colorMap[HEALTHCARE_COLORS.deepSlate],
      fontFace: 'Inter',
      align: 'center',
      bold: true
    });

    // Author title if present
    if (content.authorTitle) {
      slideObj.addText(content.authorTitle, {
        x: 1.5,
        y: 6.0,
        w: 10.333,
        h: 0.6,
        fontSize: 14,
        color: colorMap[HEALTHCARE_COLORS.warmGray],
        fontFace: 'Inter',
        align: 'center'
      });
    }

    // Speaker notes
    if (slide.speakerNotes) {
      slideObj.addNotes(slide.speakerNotes);
    }
  }

  private addClosingEmotionalSlide(slide: SlideContent) {
    const slideObj = this.pptx.addSlide({ masterName: 'HEALTHCARE_MASTER' });
    const content = slide.content;

    // Calculate appropriate font size for main message
    const textLength = content.mainMessage.length;
    let titleFontSize = 44;
    if (textLength > 60) titleFontSize = 32;
    if (textLength > 100) titleFontSize = 26;
    if (textLength > 150) titleFontSize = 22;

    // Main message
    slideObj.addText(content.mainMessage, {
      x: 0.5,
      y: 1.5,
      w: 12.5,
      h: 3,
      fontSize: titleFontSize,
      color: colorMap[HEALTHCARE_COLORS.softBlack],
      fontFace: 'Inter',
      align: 'center',
      valign: 'middle',
      bold: false,
      breakLine: true,
      wrap: true
    });

    // Call to action if present - now as subtitle text instead of button
    if (content.callToAction) {
      const ctaTextLength = content.callToAction.length;
      let ctaFontSize = 18;
      if (ctaTextLength > 80) ctaFontSize = 16;
      if (ctaTextLength > 120) ctaFontSize = 14;

      slideObj.addText(content.callToAction, {
        x: 1,
        y: 4.8,
        w: 11.5,
        h: 2,
        fontSize: ctaFontSize,
        color: colorMap[HEALTHCARE_COLORS.warmGray],
        fontFace: 'Inter',
        align: 'center',
        valign: 'top',
        breakLine: true,
        wrap: true
      });
    }

    // Speaker notes
    if (slide.speakerNotes) {
      slideObj.addNotes(slide.speakerNotes);
    }
  }

  public async exportToPPTX(): Promise<void> {
    // Process each slide
    this.deck.slides.forEach((slide) => {
      switch (slide.type) {
        case 'statement':
          this.addStatementSlide(slide);
          break;
        case 'two-column':
          this.addTwoColumnSlide(slide);
          break;
        case 'comparison-table':
          this.addComparisonTableSlide(slide);
          break;
        case 'quote':
          this.addQuoteSlide(slide);
          break;
        case 'closing-emotional':
          this.addClosingEmotionalSlide(slide);
          break;
        default:
          // Add placeholder slide for unimplemented types
          const slideObj = this.pptx.addSlide({ masterName: 'HEALTHCARE_MASTER' });
          slideObj.addText(`${slide.type} slide`, {
            x: 1,
            y: 3,
            w: 11.333,
            h: 1.5,
            fontSize: 32,
            color: colorMap[HEALTHCARE_COLORS.warmGray],
            fontFace: 'Inter',
            align: 'center'
          });
          slideObj.addText('Coming soon...', {
            x: 1,
            y: 4.5,
            w: 11.333,
            h: 1,
            fontSize: 18,
            color: colorMap[HEALTHCARE_COLORS.warmGray],
            fontFace: 'Inter',
            align: 'center'
          });
      }
    });

    // Export the presentation
    const fileName = `${this.deck.name.replace(/[^a-z0-9]/gi, '_').toLowerCase()}_${this.deck.stakeholder}.pptx`;
    await this.pptx.writeFile({ fileName });
  }

  public async exportToPDF(): Promise<void> {
    // This is a simplified PDF export - in a real implementation,
    // you'd want to render each slide to canvas and add to PDF
    const pdf = new jsPDF('landscape', 'mm', [297, 210]); // A4 landscape
    
    const fileName = `${this.deck.name.replace(/[^a-z0-9]/gi, '_').toLowerCase()}_${this.deck.stakeholder}.pdf`;
    
    // For now, create a simple PDF with slide titles
    // In production, you'd render the actual slides
    pdf.setFontSize(24);
    pdf.text(`${this.deck.name}`, 20, 30);
    pdf.setFontSize(16);
    pdf.text(`${this.deck.stakeholder} presentation`, 20, 45);
    
    let yPosition = 70;
    this.deck.slides.forEach((slide, index) => {
      pdf.setFontSize(12);
      pdf.text(`${index + 1}. ${slide.title || slide.type}`, 20, yPosition);
      yPosition += 15;
      
      if (yPosition > 180) {
        pdf.addPage();
        yPosition = 30;
      }
    });

    pdf.save(fileName);
  }
}

export const exportDeck = {
  toPPTX: async (deck: Deck): Promise<void> => {
    const exporter = new DeckExporter(deck);
    await exporter.exportToPPTX();
  },
  
  toPDF: async (deck: Deck): Promise<void> => {
    const exporter = new DeckExporter(deck);
    await exporter.exportToPDF();
  }
};